<script setup>
import { ref, computed, watch } from 'vue'
import { useEmailTemplatesStore } from '@/stores'

const props = defineProps({
  content: {
    type: String,
    default: '',
  },
  dataFieldGroup: {
    type: String,
    default: 'system',
  },
  fieldGroup: {
    type: Object,
    default: () => ({}),
  },
  preheader: {
    type: String,
    default: '',
  },
  templateId: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['update:sampleData'])

const emailTemplatesStore = useEmailTemplatesStore()

const previewHtml = ref('')
const previewError = ref('')
const loading = ref(false)
const showSampleDataEditor = ref(false)
const viewMode = ref('desktop') // 'desktop' or 'mobile'

// Initialize sample data from field group
const sampleData = ref({})

// Build sample data from field group
const buildSampleData = () => {
  if (!props.fieldGroup?.fields) return {}

  const data = { options: {} }

  props.fieldGroup.fields.forEach(field => {
    const parts = field.path.split('.')
    let current = data

    // Skip helper functions
    if (field.path.includes('(')) return

    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i]
      if (!current[part] || typeof current[part] !== 'object') {
        // Replace primitive leaves (e.g. an earlier field defined `foo` as a
        // string but a later field defines `foo.bar`) so we can keep nesting.
        current[part] = {}
      }
      current = current[part]
    }

    const lastPart = parts[parts.length - 1]

    // If this field's leaf already holds a nested object from an earlier
    // deeper path, don't clobber it with a primitive.
    if (current[lastPart] && typeof current[lastPart] === 'object' && (typeof field.sampleValue !== 'object' || field.sampleValue === null)) {
      return
    }
    current[lastPart] = field.sampleValue
  })

  return data
}

// Initialize sample data when field group changes
watch(() => props.fieldGroup, () => {
  sampleData.value = buildSampleData()
}, { immediate: true, deep: true })

// Generate preview
const generatePreview = async () => {
  if (!props.content) {
    previewHtml.value = '<p class="text-grey text-center">No content to preview</p>'
    
    return
  }

  loading.value = true
  previewError.value = ''

  try {
    const result = await emailTemplatesStore.previewContent(
      props.content,
      props.dataFieldGroup,
      sampleData.value,
      props.preheader,
    )
    previewHtml.value = result.html
  } catch (error) {
    previewError.value = error.response?.data?.details || error.message || 'Preview generation failed'
    previewHtml.value = ''
  } finally {
    loading.value = false
  }
}

// Debounced preview generation
let previewTimeout = null
watch([() => props.content, () => props.preheader, sampleData], () => {
  if (previewTimeout) clearTimeout(previewTimeout)
  previewTimeout = setTimeout(generatePreview, 500)
}, { deep: true })

// Get flat list of editable sample values
const editableSampleFields = computed(() => {
  if (!props.fieldGroup?.fields) return []

  return props.fieldGroup.fields
    .filter(field => !field.path.includes('(')) // Skip helper functions
    .map(field => ({
      ...field,
      value: getNestedValue(sampleData.value, field.path),
    }))
})

// Get nested value from object using dot notation path
const getNestedValue = (obj, path) => {
  const parts = path.split('.')
  let current = obj
  for (const part of parts) {
    if (current && typeof current === 'object' && part in current) {
      current = current[part]
    } else {
      return undefined
    }
  }
  
  return current
}

// Set nested value in object using dot notation path
const setNestedValue = (obj, path, value) => {
  const parts = path.split('.')
  let current = obj
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i]
    if (!current[part]) {
      current[part] = {}
    }
    current = current[part]
  }
  current[parts[parts.length - 1]] = value
}

// Update sample value
const updateSampleValue = (path, value) => {
  setNestedValue(sampleData.value, path, value)
  emit('update:sampleData', sampleData.value)
}

// Initial preview
generatePreview()
</script>

<template>
  <div class="preview-container">
    <!-- Preview Controls -->
    <div class="preview-controls d-flex align-center mb-3">
      <VBtnToggle
        v-model="viewMode"
        mandatory
        density="compact"
        class="mr-3"
      >
        <VBtn
          value="desktop"
          size="small"
        >
          <VIcon size="small">
            mdi-monitor
          </VIcon>
          <span class="ml-1">Desktop</span>
        </VBtn>
        <VBtn
          value="mobile"
          size="small"
        >
          <VIcon size="small">
            mdi-cellphone
          </VIcon>
          <span class="ml-1">Mobile</span>
        </VBtn>
      </VBtnToggle>

      <VBtn
        size="small"
        variant="outlined"
        @click="showSampleDataEditor = !showSampleDataEditor"
      >
        <VIcon size="small">
          mdi-pencil
        </VIcon>
        <span class="ml-1">Edit Sample Data</span>
      </VBtn>

      <VSpacer />

      <VBtn
        size="small"
        variant="text"
        :loading="loading"
        @click="generatePreview"
      >
        <VIcon size="small">
          mdi-refresh
        </VIcon>
        <span class="ml-1">Refresh</span>
      </VBtn>
    </div>

    <!-- Sample Data Editor -->
    <VExpandTransition>
      <VCard
        v-if="showSampleDataEditor"
        class="mb-3 pa-3 sample-data-editor"
        variant="outlined"
      >
        <h5 class="mb-2">
          Sample Data
        </h5>
        <p class="text-caption text-grey mb-3">
          Edit the sample values below to customize the preview
        </p>
        <div class="sample-fields-grid">
          <template
            v-for="field in editableSampleFields"
            :key="field.path"
          >
            <VTextField
              :model-value="field.value"
              :label="field.label"
              :placeholder="String(field.sampleValue)"
              density="compact"
              variant="outlined"
              hide-details
              class="mb-2"
              @update:model-value="(v) => updateSampleValue(field.path, v)"
            />
          </template>
        </div>
      </VCard>
    </VExpandTransition>

    <!-- Preview Error -->
    <VAlert
      v-if="previewError"
      type="error"
      density="compact"
      class="mb-3"
      closable
      @click:close="previewError = ''"
    >
      <strong>Preview Error:</strong> {{ previewError }}
    </VAlert>

    <!-- Preview Frame -->
    <div
      class="preview-frame"
      :class="{
        'preview-mobile': viewMode === 'mobile',
        'preview-desktop': viewMode === 'desktop',
      }"
    >
      <div
        v-if="loading"
        class="preview-loading"
      >
        <VProgressCircular
          indeterminate
          color="primary"
        />
        <p class="mt-2 text-grey">
          Generating preview...
        </p>
      </div>
      <iframe
        v-else
        :srcdoc="previewHtml"
        class="preview-iframe"
        sandbox="allow-same-origin"
        frameborder="0"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.preview-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.preview-controls {
  flex-shrink: 0;
}

.sample-data-editor {
  max-height: 300px;
  overflow-y: auto;
}

.sample-fields-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 8px;
}

.preview-frame {
  flex: 1;
  border: 1px solid var(--ds-border-default);
  border-radius: 8px;
  overflow: hidden;
  background-color: var(--ds-surface-card);
  min-height: 400px;
  transition: all 0.3s ease;
}

.preview-desktop {
  width: 100%;
}

.preview-mobile {
  max-width: 375px;
  margin: 0 auto;
}

.preview-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 300px;
}

.preview-iframe {
  width: 100%;
  height: 100%;
  min-height: 400px;
  border: none;
}
</style>
